<template>
  <div class="FalecidoSV">
    <!-- Primeira linha do formulário -->
    <div class="row no-gutters">
      <div class="form-group">
        <span class="custom-control custom-switch mr-4">
          <input id="pessoaDescon"
                 ref="pessoaDescon"
                 v-model="registro.pessoa.pessoa_desconhecida"
                 class="custom-control-input"
                 name="pessoaDescon"
                 type="checkbox"
                 value="O"
                 :disabled="registro.pessoa.natimorto"
                 @change="definirTipoRegistroObito"
                 @keydown.enter="changeDesconhecido()">
          <label class="custom-control-label"
                 for="pessoaDescon">Pessoa desconhecida</label>
        </span>
      </div>
      <div class="form-group">
        <span class="custom-control custom-switch">
          <input id="natimorto"
                 ref="natimorto"
                 v-model="registro.pessoa.natimorto"
                 class="custom-control-input"
                 name="natimorto"
                 type="checkbox"
                 value="NT"
                 :disabled="registro.pessoa.pessoa_desconhecida ||
                   (registro.tipo_registro === 'NT' && registro.livro && registro.livro.tipo_livro === 'NT') ||
                   (registro.tipo_registro === 'O' && registro.livro && registro.livro.tipo_livro === 'O')"
                 @change="definirTipoRegistroNT"
                 @keyup.enter="changeNatimorto()">
          <label class="custom-control-label"
                 for="natimorto">Natimorto</label>
        </span>
      </div>
    </div>

    <!-- CPF, sem inscrição -->
    <div class="row">
      <div class="col-sm-4 col-lg-3">
        <div class="form-group">
          <label class="label"
                 for="cpf">CPF
          </label>
          <div class="input-group">
            <input id="cpf"
                   ref="falecido_cpf"
                   v-model="registro.pessoa.cpf"
                   v-mask="`999.999.999-99`"
                   v-validate="{ required: !registro.pessoa.cpf_sem_inscricao, regex: /\d{3}\.\d{3}\.\d{3}-\d{2}$/ }"
                   :class="{ 'is-invalid': errors.has('cpf')}"
                   class="form-control form-control-sm"
                   name="cpf"
                   placeholder="000.000.000-00"
                   type="tel"
                   :disabled="!!registro.pessoa.pessoa_desconhecida || !!registro.pessoa.natimorto || !!registro.pessoa.cpf_sem_inscricao"
                   @input="checkCPF()">
            <div class="input-group-append">
              <button class="btn btn-sm btn-primary"
                      :disabled="!!registro.pessoa.pessoa_desconhecida || !!registro.pessoa.natimorto || !!registro.pessoa.cpf_sem_inscricao"
                      @click="showModal = true">
                Averbar
              </button>
            </div>
          </div>
          <small v-if="errors.has('cpf')"
                 class="invalid-feedback">{{ errors.first('cpf') }}</small>
        </div>
      </div>

      <div class="col-sm-4 col-lg-3">
        <div class="form-group">
          <label class="mb-4"
                 for="cpf_sem_inscricao" />
          <span class="custom-control custom-switch">
            <input id="cpf_sem_inscricao"
                   v-model="registro.pessoa.cpf_sem_inscricao"
                   class="custom-control-input"
                   name="cpf_sem_inscricao"
                   type="checkbox"
                   :value="1"
                   :disabled="registro.pessoa.pessoa_desconhecida || registro.pessoa.natimorto"
                   @change="[registro.pessoa.cpf = null, changed()]"
                   @keyup.enter="changeSemCPF()">
            <label class="custom-control-label"
                   for="cpf_sem_inscricao">Sem inscrição</label>
          </span>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-6">
        <div class="form-group">
          <label class="label"
                 for="nome">Nome
          </label>
          <input id="nome"
                 v-model.lazy="registro.pessoa.nome"
                 v-uppercase
                 v-validate="`required`"
                 :class="{ 'is-invalid': errors.has('nome')}"
                 class="form-control form-control-sm text-uppercase"
                 name="nome"
                 placeholder="Valentina Alves Silva"
                 type="text"
                 @change="validateInputs('nome')">
          <small v-if="errors.has('nome')"
                 class="invalid-feedback">{{ errors.first('nome') }}</small>
        </div>
      </div>

      <div class="col-sm-3 col-lg-3">
        <div class="form-group">
          <label class="label"
                 for="data_nascimento">Data de nascimento</label>
          <input id="data_nascimento"
                 v-model="registro.pessoa.data_nascimento"
                 v-validate="`date_between:1500-01-01,${limitDate}|max_date:${new Date()}`"
                 :class="{ 'is-invalid': errors.has('data_nascimento')}"
                 class="form-control form-control-sm"
                 name="data_nascimento"
                 type="date"
                 :disabled="registro.pessoa.pessoa_desconhecida || registro.pessoa.natimorto"
                 @input="changeIdade">
          <small v-if="errors.has('data_nascimento')"
                 class="invalid-feedback">{{ errors.first('data_nascimento') }}</small>
        </div>
      </div>

      <div class="col-sm-3 col-lg-3">
        <div class="form-group">
          <label class="label"
                 for="idade">Idade</label>
          <p class="mt-1">
            <span v-if="errors.has('data_nascimento')"
                  class="text-danger">Data inválida</span>
            <span v-else
                  class="label">
              {{ idade_tipo }}
            </span>
          </p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-3">
        <div class="form-group">
          <label class="label"
                 for="sexo">Sexo</label>
          <select id="sexo"
                  v-model="registro.pessoa.sexo"
                  class="custom-select custom-select-sm"
                  name="sexo"
                  @change="changed">
            <option value=""
                    selected>
              Selecione
            </option>
            <option v-for="(sexo, ind) in SEXOS"
                    :key="ind"
                    :value="sexo.value">
              {{ sexo.name }}
            </option>
          </select>
          <small v-if="errors.has('sexo')"
                 class="invalid-feedback">{{ errors.first('sexo') }}</small>
        </div>
      </div>
      <div class="col-sm-3">
        <div class="form-group">
          <label class="label"
                 for="cor_pele">Cor de pele</label>
          <select id="cor_pele"
                  v-model="registro.pessoa.cor_pele"
                  class="custom-select custom-select-sm"
                  name="cor_pele"
                  @change="changed">
            <option value=""
                    selected>
              Selecione
            </option>
            <option v-for="(cor, ind) in CORES_PELE"
                    :key="ind"
                    :value="cor.value">
              {{ cor.name }}
            </option>
          </select>
          <small v-if="errors.has('cor_pele')"
                 class="invalid-feedback">{{ errors.first('cor_pele') }}</small>
        </div>
      </div>

      <div class="col-sm-3">
        <div class="form-group">
          <label class="label"
                 for="estado_civil">Estado Civil</label>
          <select id="estado_civil"
                  v-model="registro.pessoa.estado_civil"
                  class="custom-select custom-select-sm"
                  :disabled="registro.pessoa.pessoa_desconhecida"
                  name="estado_civil"
                  @change="changed">
            <option v-for="(estado, ind) in ESTADOS_CIVIL.filter(est => (registro.pessoa.sexo && registro.pessoa.sexo !='I' )? est.genero == registro.pessoa.sexo : est.value )"
                    :key="ind"
                    :value="estado.value">
              {{ estado.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="col-sm-3">
        <div class="form-group">
          <label class="label"
                 for="nacionalidade">Nacionalidade</label>
          <Nacionalidades id="nacionalidade"
                          v-model="registro.pessoa.nacionalidade_codigo"
                          :prop-nacionalidade="registro.pessoa.nacionalidade"
                          :prop-sexo="registro.pessoa.sexo"
                          @updated="nacionalidadeChanged" />
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-2"
           :class="{'d-none': registro.pessoa.nacionalidade && registro.pessoa.nacionalidade.codigo != 76}">
        <div class="form-group">
          <label class="label"
                 for="uf">UF</label>
          <v-select :id="`uf-${_uid}`"
                    v-model="registro.pessoa.uf"
                    class="form-control form-control-sm"
                    name="ufFalecido"
                    placeholder="Selecione"
                    :options="selectsOptions.states.map((state)=>state.uf)"
                    @input="ufChanged" />
        </div>
      </div>
      <div v-if="show_naturalidade"
           class="col-4">
        <div class="form-group">
          <label class="label"
                 for="naturalidade">Naturalidade</label>
          <v-select :id="`naturalidade-${_uid}`"
                    v-model="registro.pessoa.naturalidade"
                    class="form-control form-control-sm"
                    name="naturalidadeFalecido"
                    placeholder="Selecione"
                    :options="citiesFiltered.map((city)=>city.nome)"
                    @input="naturalidadeChanged" />
        </div>
      </div>
      <div v-if="showNatDeclarada"
           class="col-4 col-sm-6">
        <div class="form-group">
          <label class="label"
                 for="naturalidade_declarada">Naturalidade declarada pelo cidadão</label>
          <input id="naturalidade_declarada"
                 v-model="registro.pessoa.naturalidade_declarada"
                 class="form-control form-control-sm"
                 name="naturalidade_declarada"
                 type="text"
                 :disabled="registro.pessoa.pessoa_desconhecida"
                 @change="changed">
        </div>
      </div>
      <div v-if="propObito"
           class="col-2 d-flex align-items-center">
        <span class="custom-control custom-switch custom-control-inline mr-4">
          <input id="eleitor_ativo"
                 v-model="propObito.eleitor_ativo"
                 class="custom-control-input"
                 name="eleitor_ativo"
                 type="checkbox">
          <label
            class="custom-control-label"
            for="eleitor_ativo">Eleitor Ativo</label>
        </span>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="SegundaViaObito_body_header mb-2 w-100">
          Endereço
        </div>
      </div>
      <div class="col-12">
        <BoxEndereco :prop-mostrar-exterior="true"
                     :endereco-disable="true"
                     :no-header="true"
                     :data="registro.pessoa.endereco"
                     @onEnderecoAdd="getAddress" />
      </div>
    </div>
    <div class="SegundaViaObito_body_header mb-2">
      Genitores
    </div>
    <div v-for="(genitor, ind) in registro.pessoa.genitores"
         :key="ind">
      <GenitorSVCasamento :prop-genitor="genitor"
                          :prop-index-pessoa="0"
                          :prop-index-genitor="ind"
                          @removeGenitor="removeGenitor(ind)" />
    </div>
    <div class="row mb-2">
      <div class="col-12">
        <button class="btn btn-sm btn-outline-primary btn-block btn-new-genitor"
                @click="newGenitor()">
          <IconSvg class="icon-add icon--sm mr-1"
                   icon="add" />
          Adicionar genitor
        </button>
      </div>
    </div>

    <div class="SegundaViaObito_body_header mb-2">
      Documentos
    </div>
    <Documentos :prop-data="{...propObito, data_nascimento: data_nascimento}"
                :pessoa-id="registro.pessoa.pessoa_id"
                :documentos="registro.pessoa.documentos"
                :tipo-registro="registro.tipo_registro"
                :prop-hide-swal="true"
                @DocumentoAdded="getDocumentos"
                @DocumentoRemoved="removeDocumento" />

    <b-modal v-if="showModal"
             :visible="true"
             centered
             size="xl"
             no-close-on-backdrop
             no-close-on-esc
             :hide-header="true"
             :hide-footer="true">
      <AverbacaoSV :prop-cpf="registro.pessoa.cpf"
                   :registro-id="registro.registro_id"
                   :prop-transporte="registro.transporte"
                   @hideModal="showModal = false" />
    </b-modal>
  </div>
</template>

<script>
  /* components */
  import Nacionalidades from "@/components/SofiaRei/Nacionalidades"
  import BoxEndereco from '@/components/SofiaRei/BoxEndereco.vue'
  import GenitorSVCasamento from "@/components/SofiaRei/Casamento/SegundaVia/GenitorSVCasamento.vue"
  import Documentos from '@/components/SofiaRei/Obito/Passos/Sessoes/Documentos.vue'
  import AverbacaoSV from '@/components/SofiaRei/Nascimento/SegundaVia/AverbacaoSV'

  /* services */
  import EnderecosService from "@/services/Enderecos"
  import PessoasService from "@/services/Pessoas"

  /* constants */
  import { ESTADOS_CIVIL } from "@/constants/estadosCivil"
  import SEXOS from "@/constants/sexos"
  import TIPOS_IDADE from "@/constants/tiposIdade"
  import CORES_PELE from "@/constants/coresPele"
  import BrazilianStates from "@/constants/brazilianStates"


  const constantes = { DESCONHECIDO_NATIMORTO, DESCONHECIDO, NATIMORTO, SEXOS, TIPOS_IDADE, ESTADOS_CIVIL, CORES_PELE }
  const DESCONHECIDO_NATIMORTO = "pessoa_desconhecida_natimorto"
  const DESCONHECIDO = "pessoa_desconhecida"
  const NATIMORTO = "natimorto"

  /* filters */
  import { filterDate, filterMaskedNumber } from '@/filters/filters'

  /* outros */
  import moment from "moment"

  export default {
    name: 'FalecidoSV',
    components: {
      Nacionalidades,
      BoxEndereco,
      GenitorSVCasamento,
      Documentos,
      AverbacaoSV
    },
    props: {
      propRegistro: { type: Object, default: null},
      propObito: { type: Object, default: null}
    },
    data () {
      return {
        ...constantes,
        disable_cpf: false,
        show_naturalidade: true,
        naturalidade: "",
        cpfNumber: '',
        desconhecidoDisabled: false,
        natimortoDisabled: false,
        desconhecidoNatimortoDisabled: false,
        deleteds: { genitores: [], anteriores: [], enderecos: [] },
        selectsOptions: { states: [...BrazilianStates], cities: [], nacionalidades: [] },
        citiesFiltered: [],
        idade_tipo: null,
        limitDate: moment(new Date()).add(1, 'day').format('YYYY-MM-DD'),
        registro: {
          pessoa: {}
        },
        showModal: false,
        data_nascimento: null
      }
    },
    computed: {
      showNatDeclarada () {
        let { nacionalidade_codigo } = this.registro.pessoa

        if (nacionalidade_codigo && nacionalidade_codigo != 76) {
          return true
        }

        return (this.naturalidade === 'Outro')
      }
    },
    mounted () {
      this.init()
    },
    methods: {
      async init () {
        this.selectsOptions.cities = await EnderecosService.getCities()

        this.registro = { ...this.propRegistro }

        if (this.registro.pessoa.uf) {
          this.filterCities()
        }

        this.changeIdade()
        

        if (this.registro.pessoa.nacionalidade_codigo != 76) {
          this.show_naturalidade = false
        }

        if (this.registro.pessoa.data_nascimento) {
          this.data_nascimento = moment(this.registro.pessoa.data_nascimento).format('DD/MM/YYYY')
        }
      },
      changed () {
        this.$emit('changed', this.registro)
      },
      changeDesconhecido () {
        this.registro.pessoa.pessoa_desconhecida = !this.registro.pessoa.pessoa_desconhecida

        this.definirTipoRegistroObito()
      },
      changeNatimorto () {
        this.registro.pessoa.natimorto = !this.registro.pessoa.natimorto

        this.definirTipoRegistroNT()
      },
      changeSemCPF () {
        this.registro.pessoa.cpf_sem_inscricao = !this.registro.pessoa.cpf_sem_inscricao

        this.registro.pessoa.cpf = null

        this.changed()
      },
      filterCities () {
        if (!this.selectsOptions && !this.selectsOptions.cities) return []

        if (this.registro.pessoa.uf) {
          const state = this.selectsOptions.states.find(state => state.name === this.registro.pessoa.uf)

          /**
           * Fazemos essa verificação pois na migração alguns registros não
           * trazem a UF em sigla e sim com o nome completo
           */
          if (state) {
            this.registro.pessoa.uf = state.uf
          }

          const stateSelected = this.selectsOptions.states.find(state => state.uf == this.registro.pessoa.uf)
          this.citiesFiltered = this.selectsOptions.cities.filter(city => city.nome === 'Outro' || city.codigo_uf == stateSelected.ibgeId)
        }
      },
      changeIdade () {
        if (!this.registro.pessoa.data_nascimento) {
          this.registro.pessoa = {
            ...this.registro.pessoa,
            idade: null,
            idade_tipo: null
          }

          this.idade_tipo = "Informe a data de nascimento"

          return this.changed()
        }
        const dataNascimento = moment(this.registro.pessoa.data_nascimento).format('YYYY-MM-DD')

        let dataComparacao = moment()

        if (this.registro.data_ocorrido) {
          dataComparacao = moment(this.registro.data_ocorrido)
        }

        const diferenciaEmDias = moment(dataComparacao).diff(dataNascimento, 'days')
        const diferenciaEmMeses = moment(dataComparacao).diff(dataNascimento, 'months')
        const diferenciaEmAnos = moment(dataComparacao).diff(dataNascimento, 'years')

        // fazemos todas as validações em dias, pois dependendo o mês e ano o resultado do moment() não sai preciso
        if (diferenciaEmDias < 30 || diferenciaEmMeses < 1) {
          return this.updateIdade(diferenciaEmDias, 'D', `${diferenciaEmDias} ${diferenciaEmDias <= 1 ? 'dia' : 'dias'}`)
        }

        if (diferenciaEmMeses >= 1 && diferenciaEmMeses < 12) {
          return this.updateIdade(diferenciaEmMeses, 'M', `${diferenciaEmMeses} ${diferenciaEmMeses === 1 ? 'mês' : 'meses'}`)
        }

        if (diferenciaEmAnos >= 1) {
          return this.updateIdade(diferenciaEmAnos, 'A', `${diferenciaEmAnos} ${diferenciaEmAnos === 1 ? 'ano' : 'anos'}`)
        }
      },
      updateIdade (idade, tipoIdadeToLocalData, tipoIdadeToData) {
        this.registro.pessoa = {
          ...this.registro.pessoa,
          idade: idade,
          idade_tipo: tipoIdadeToLocalData
        }

        this.idade_tipo = tipoIdadeToData

        this.changed()
      },
      definirTipoRegistroNT () {
        this.localData.tipo_registro_obito = 'NT'

        if (this.registro.pessoa.natimorto) {
          this.registro.pessoa.data_nascimento = null
          this.registro.pessoa.idade = null
          this.registro.pessoa.idade_tipo = null
          this.registro.pessoa.cpf = null
          this.registro.pessoa.cpf_sem_inscricao = true
          this.registro.pessoa.ocupacao_declarada = null
          this.registro.pessoa.ocupacao_codigo = null
        }

        this.changed()
      },
      definirTipoRegistroObito () {
        this.localData.tipo_registro_obito = 'O'

        if (this.registro.pessoa.pessoa_desconhecida) {
          this.registro.pessoa.nome = 'DESCONHECIDO'
          this.registro.pessoa.data_nascimento = null
          this.registro.pessoa.idade = ''
          this.registro.pessoa.idade_tipo = null
          this.registro.pessoa.cpf = null
          this.registro.pessoa.cpf_sem_inscricao = true
          this.registro.pessoa.uf = ''
          this.registro.pessoa.ocupacao_declarada = ''
          this.registro.pessoa.ocupacao_codigo = null
          this.registro.pessoa.naturalidade = ''
          this.naturalidade = ''
          this.registro.pessoa.estado_civil = 'IGNORADO'
        } else {
          this.registro.pessoa.nome = ''
        }

        this.changed()
      },
      naturalidadeChanged () {
        this.registro.pessoa.naturalidade = this.naturalidade

        if (this.registro.pessoa) {
          this.registro.pessoa.naturalidade = this.naturalidade
        } else {
          this.registro = {
            ...this.registro,
            pessoa: {
              naturalidade: this.naturalidade
            }
          }
        }

        this.changed()
      },
      ocupacaoChanged () {
        this.profissaoPreload = this.registro.pessoa.ocupacao_declarada
        this.changed()
      },
      ufChanged () {
        this.naturalidade = ''
        this.changed()

        this.filterCities()

        this.verifyReset()
      },
      nacionalidadeChanged (nacionalidade) {
        this.registro.pessoa.nacionalidade = nacionalidade
        if (this.registro.pessoa.nacionalidade.nome === "Brazil" || this.registro.pessoa.nacionalidade.nome === "Brasil") {
          this.registro.pessoa.uf = ""
          this.show_naturalidade = true
          this.registro.pessoa.naturalidade_declarada =  null
        } else {
          this.show_naturalidade = false
          this.registro.pessoa.uf = "EX"
        }
        this.changed()
      },
      tipoIdadeChanged ({target: { value }}) {
        this.registro.pessoa.idade_tipo = value
      },
      async checkCPF () {
        let cpf = filterMaskedNumber(this.registro.pessoa.cpf)

        if (cpf && cpf.length < 11) return

        try {
          const busca = await PessoasService.getCadastroByCPF(cpf)

          const [pessoa] = busca.data

          if (pessoa &&  pessoa.obito && pessoa.obito.obito_id != this.$route.params.id) {
            objSwal.error.title = "CPF já cadastrado!"
            objSwal.error.text = "Este CPF já possui um registro de óbito, você será redirecinado para o registro."

            return Swal.fire(objSwal.error).then(() => {
              this.$router.push({ name: 'resumo_obito', params: { id: pessoa.obito.obito_id }})
            })
          } else {

            const dados = await PessoasService.getPessoaByCPF(cpf)

            if (dados.isEmpty) {
              this.changed()
              return
            }

            const { nome, datanascimento, sexo, nomemae } = dados
            const sexos = {
              1: 'M',
              2: 'F'
            }

            if (nomemae) {
              // deixando sexo feminino temporário para se adaptar ao que vem da CRC
              const mother = this.registro.pessoa.genitores.find(genitor => genitor.pessoa.nome == nomemae)
              if (!mother) {
                this.registro.pessoa.genitores.push({ pessoa: { nome: nomemae, sexo: "F" } })
              }
            }

            this.registro.pessoa.nome = nome
            this.registro.pessoa.data_nascimento = filterDate(datanascimento)

            this.changeIdade()

            this.registro.pessoa.sexo = sexos[sexo] || 'I'
          }
        } catch (error) {
          objSwal.error.title = 'Erro ao buscar CPF!'
          objSwal.error.text = error.message
          this.changed()
          return Swal.fire(objSwal.error)
        }
      },
      getAddress (endereco) {
        this.registro.pessoa.endereco = endereco
      },
      removeGenitor (index) {
        const { genitores } = this.registro.pessoa

        if (genitores[index] && genitores[index].genitor_id) {
          this.$emit('removeGenitor',genitores[index].genitor_id)
        }

        this.registro.pessoa.genitores.splice(index, 1)
      },
      newGenitor () {
        this.registro.pessoa.genitores.push({
          filiacao: [
            { nome: '' },
            { nome: '' }
          ],
          localOcorrenciaFalecimento: {
            tipo: 'OUTRO',
            endereco: {}
          },
          pessoa: {
            cpf: null,
            sexo: null,
            nome: null,
            endereco: {},
            documentos: [],
            data_nascimento: null,
            idade: 0,
            codigo_ocupacao: [],
            nacionalidade_codigo: 76,
            nacionalidade: {
              nacionalidade_codigo: 76,
              nome: 'Brasil',
              nome_masculino: 'brasileiro',
              nome_feminino: 'brasileira'
            },
            falecido: false
          },
          declarante: false
        },)
      },
      getDocumentos (documento) {
        if (!this.registro.pessoa.documentos) {
          this.registro.pessoa.documentos = []
        }
        
        this.registro.pessoa.documentos.push(documento)
        this.changed()
      },

      removeDocumento (documento) {
        this.registro.pessoa.documentos.splice(documento.localId, 1)
        this.$emit('documentoRemoved', documento)
      },
    }
  }
</script>

<style>

</style>